/**
 * Offline Indicator — Shows banner when user is offline
 * Spec ref: SPEC-025 §3.4 (Offline Indicator Component),
 *           §2.3 (Offline Experience)
 *
 * - Online: renders nothing (null)
 * - Offline: fixed bottom banner "Sin conexión"
 * - Auto-updates on online/offline events
 */

'use client';

import { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

export function OfflineIndicator() {
  // 4E-MED-002 fix: initialize to `true` unconditionally to match SSR
  // (during SSR, `navigator` is undefined, so we assume online). Then
  // update to the actual `navigator.onLine` in a useEffect (client-only).
  // Pre-fix, the conditional `typeof navigator !== 'undefined' ?
  // navigator.onLine : true` produced different values on server (true)
  // vs client (navigator.onLine, which may be false on initial render
  // if the browser is offline), causing a hydration mismatch warning.
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    // Sync with the actual browser state on mount.
    setIsOnline(navigator.onLine);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div suppressHydrationWarning className="fixed bottom-4 left-4 right-4 z-40 bg-amber-500 text-amber-950 px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-medium text-sm shadow-lg max-w-lg mx-auto">
      <WifiOff className="size-4 shrink-0" />
      <span>Sin conexión — algunas funciones no están disponibles</span>
    </div>
  );
}

/**
 * Inline status indicator for specific areas (e.g., wallet, conference).
 * Shows a small dot + text instead of a banner.
 */
export function ConnectionStatus() {
  // 4E-MED-002: same SSR hydration fix as OfflineIndicator.
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    setIsOnline(navigator.onLine);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <span suppressHydrationWarning className="inline-flex items-center gap-1.5 text-xs">
      {isOnline ? (
        <>
          <Wifi className="size-3 text-green-500" />
          <span className="text-muted-foreground">Conectado</span>
        </>
      ) : (
        <>
          <WifiOff className="size-3 text-amber-500" />
          <span className="text-amber-600">Sin conexión</span>
        </>
      )}
    </span>
  );
}
